import React, { useState, useRef, useEffect, useMemo } from "react";
import { lighten } from "@mui/material";
/* eslint-disable import/no-unresolved */
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
} from "recharts";
import { DEFAULT_VISUAL_CONFIG, APPS, WORKSPACES, FROSTED_PANEL_STYLE, FROSTED_POPOVER_STYLE } from "../../CompanyMetrics/constants";
import { GET_IMAGE } from "../../../helpers/images";
import { GMB_LOGO_URL } from "../../../constants/branding";
import { UseCompanyColors } from "../../../hooks/company";
import MetricDetailsModal from "../../CompanyMetrics/MetricDetailsModal";
import MetricSparkline from "../../CompanyMetrics/MetricSparkline";

const PremiumRobotIcon = ({ className, isHovered }) => (
  <div className={`robot-icon-container flex items-center justify-center transition-all duration-700 ${className} ${isHovered ? "rotate-y-active" : ""}`}>
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full drop-shadow-sm">
      <defs>
        <linearGradient id="robotBodyGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="var(--gmb-primary)" stopOpacity="0.55" />
          <stop offset="100%" stopColor="var(--gmb-primary)" />
        </linearGradient>
        <filter id="robotGlow" x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="0.8" result="blur" />
          <feComposite in="SourceGraphic" in2="blur" operator="over" />
        </filter>
      </defs>
      <rect x="6" y="5" width="12" height="9" rx="3.5" fill="url(#robotBodyGradient)" />
      <rect x="7.5" y="6.5" width="9" height="6" rx="2" fill="white" fillOpacity="0.1" />
      <circle cx="10" cy="9.5" r="1.2" fill="white" filter="url(#robotGlow)" />
      <circle cx="14" cy="9.5" r="1.2" fill="white" filter="url(#robotGlow)" />
      <path d="M12 5V3M10 3h4" stroke="var(--gmb-primary)" strokeWidth="1.5" strokeLinecap="round" />
      <rect x="5" y="15" width="14" height="5" rx="2.5" fill="url(#robotBodyGradient)" />
      <rect x="7.5" y="16.5" width="10" height="2" rx="1" fill="white" fillOpacity="0.1" />
      <rect x="10.5" y="14" width="3" height="1.5" fill="var(--gmb-primary)" fillOpacity="0.8" />
    </svg>
  </div>
);

const DEFAULT_POINTS_RULE = { metricAmount: 1, pointsEarned: 10 };

function RuleModal({ isOpen, onClose, metric, canEdit = false, onSaveGuideText, onSaveDailyGoalText }) {
  const gameConfig = metric?.gameConfig || {};
  // Guide text / daily goal feed the TV leaderboard labels: guide text renders
  // as the grey pill under the metric name, daily goal as the teal pill.
  const savedGuideText = gameConfig.guideText ?? metric?.guideText ?? "";
  const savedDailyGoalText = gameConfig.dailyGoalText ?? metric?.dailyGoalText ?? "";
  const [guideText, setGuideText] = useState(savedGuideText);
  const [dailyGoalText, setDailyGoalText] = useState(savedDailyGoalText);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setGuideText(savedGuideText);
      setDailyGoalText(savedDailyGoalText);
      setError(null);
    }
  }, [isOpen, savedGuideText, savedDailyGoalText]);

  if (!isOpen) return null;
  const dailyTarget = gameConfig.dailyTarget ?? 100;
  const pointsRule = gameConfig.pointsRule || DEFAULT_POINTS_RULE;
  const metricAmount = pointsRule.metricAmount ?? 1;
  const pointsEarned = pointsRule.pointsEarned ?? 10;
  const unitLabel = metric?.unit === "currency" ? "$" : "units";

  const trimmedGuide = guideText.trim();
  const trimmedGoal = dailyGoalText.trim();
  const isDirty =
    trimmedGuide !== (savedGuideText || "").trim() ||
    trimmedGoal !== (savedDailyGoalText || "").trim();

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      if (trimmedGuide !== (savedGuideText || "").trim() && onSaveGuideText) {
        await onSaveGuideText(metric, trimmedGuide);
      }
      if (trimmedGoal !== (savedDailyGoalText || "").trim() && onSaveDailyGoalText) {
        await onSaveDailyGoalText(metric, trimmedGoal);
      }
      onClose?.();
    } catch (err) {
      setError(err?.message || "Could not save changes.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[150] flex items-center justify-center p-4 bg-slate-900/15 backdrop-blur-[3px]" onClick={onClose}>
      <div className="rounded-lg w-full max-w-[340px] overflow-hidden p-[20px] max-h-[85vh] overflow-y-auto" style={FROSTED_PANEL_STYLE} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2.5 mb-[16px]">
          <div className="w-9 h-9 rounded-lg bg-[var(--gmb-primary-soft)] text-[var(--gmb-primary)] flex items-center justify-center flex-shrink-0">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5"><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="4" /><circle cx="12" cy="12" r="1" fill="currentColor" /></svg>
          </div>
          <h3 className="text-[15px] font-semibold text-gray-900">Gamification rules</h3>
        </div>
        <div className="space-y-2.5">
          <div className="px-3.5 py-[12px] bg-[var(--gmb-primary-soft)] rounded-[10px] border border-[var(--gmb-primary-tint)] flex items-center justify-between gap-[12px]">
            <span className="text-xs font-medium text-[var(--gmb-primary)] flex-shrink-0">Target goal</span>
            <span className="text-base font-semibold text-gray-900 text-right">{dailyTarget} {unitLabel} per day</span>
          </div>
          <div className="px-3.5 py-[12px] bg-emerald-50/60 rounded-[10px] border border-emerald-100">
            <p className="text-xs font-medium text-emerald-600 mb-1">Points earned</p>
            <p className="text-[13px] text-gray-600 leading-snug">
              Every <span className="text-gray-900 font-medium">{metricAmount} {unitLabel}</span> earns <span className="text-[var(--gmb-primary)] font-medium">{pointsEarned} pts</span>
            </p>
          </div>
          <div className="px-3.5 py-[12px] bg-violet-50/60 rounded-[10px] border border-violet-100">
            <p className="text-xs font-medium text-violet-600 mb-1">Guide text</p>
            {canEdit ? (
              <>
                <input
                  type="text"
                  value={guideText}
                  onChange={(e) => setGuideText(e.target.value)}
                  placeholder="e.g. $1000 / 100 POINTS, 50 PTS / HR"
                  className="w-full bg-white border border-violet-100 px-3 py-2 rounded-lg text-sm font-medium text-gray-700 focus:border-violet-400 focus:ring-2 focus:ring-violet-50 outline-none placeholder:text-gray-400 placeholder:font-normal"
                />
                <p className="text-[10px] text-gray-400 mt-1.5">Shown on the TV leaderboard under the metric name.</p>
              </>
            ) : (
              <p className={`text-[13px] leading-snug ${savedGuideText.trim() ? "text-gray-600" : "text-gray-400 italic"}`}>
                {savedGuideText.trim() || "No guide on how this metric works yet."}
              </p>
            )}
          </div>
          <div className="px-3.5 py-[12px] bg-teal-50/60 rounded-[10px] border border-teal-100">
            <p className="text-xs font-medium text-teal-600 mb-1">Daily goal</p>
            {canEdit ? (
              <>
                <input
                  type="text"
                  value={dailyGoalText}
                  onChange={(e) => setDailyGoalText(e.target.value)}
                  placeholder="e.g. $1,500/DAY, 3/DAY, EVERY 2 DAYS"
                  className="w-full bg-white border border-teal-100 px-3 py-2 rounded-lg text-sm font-medium text-gray-700 focus:border-teal-500 focus:ring-2 focus:ring-teal-50 outline-none placeholder:text-gray-400 placeholder:font-normal"
                />
                <p className="text-[10px] text-gray-400 mt-1.5">Shown as a teal pill on the TV leaderboard.</p>
              </>
            ) : (
              <p className={`text-[13px] leading-snug ${savedDailyGoalText.trim() ? "text-gray-600" : "text-gray-400 italic"}`}>
                {savedDailyGoalText.trim() || "No daily goal set."}
              </p>
            )}
          </div>
          {error && (
            <p className="text-sm font-medium text-pink-600 bg-pink-50 border border-pink-100 rounded-[10px] px-3 py-2">{error}</p>
          )}
        </div>
        <div className="flex gap-2.5 mt-[16px]">
          {canEdit && (
            <button
              type="button"
              onClick={handleSave}
              disabled={!isDirty || saving}
              className="flex-1 py-2.5 bg-[var(--gmb-primary)] text-white rounded-lg font-medium text-sm hover:brightness-95 transition-all disabled:opacity-40 disabled:pointer-events-none"
            >
              {saving ? "Saving…" : "Save"}
            </button>
          )}
          <button type="button" onClick={onClose} className={`py-2.5 rounded-lg font-medium text-sm transition-all ${canEdit ? "flex-1 bg-gray-900 text-white hover:bg-black" : "w-full bg-[var(--gmb-primary)] text-white hover:brightness-95"}`}>Close</button>
        </div>
      </div>
    </div>
  );
}

/** Resolve app logo by source code (same as workspace-kpi Kpi.jsx getLogo). */
function getLogoFromIntegrationApps(integrationApps, source) {
  if (!Array.isArray(integrationApps) || !source) return GMB_LOGO_URL;
  const matchingApp = integrationApps.find((app) => (app.code || app.id) === source);
  return matchingApp?.logo ? GET_IMAGE(matchingApp.logo) || matchingApp.logo : GMB_LOGO_URL;
}

/**
 * Shared dashboard MetricCard used by both Workspace metrics and Company metrics (updated UI).
 */
function MetricCard({
  metric,
  userRole,
  onRemove,
  onEdit,
  onGamify,
  onAssign,
  onGamifyForWorkspace,
  onSaveGuideText,
  onSaveDailyGoalText,
  isGlobalView = false,
  isPreview = false,
  assignedWorkspaces = [],
  allWorkspaces = [],
  integrationApps = [],
  allMetrics = [],
  rawKpi = null,
  rawKpis = [],
}) {
  const [showMenu, setShowMenu] = useState(false);
  const [showRules, setShowRules] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const menuRef = useRef(null);
  // Use metric.appearance (appearance type from server) to drive chart/card type; fallback to visualConfig then default
  const baseConfig = metric.visualConfig || DEFAULT_VISUAL_CONFIG;
  let appearanceType = baseConfig.type;
  if (typeof metric.appearance === "string") {
    appearanceType = metric.appearance;
  } else if (metric.appearance && typeof metric.appearance === "object" && typeof metric.appearance.type === "string") {
    appearanceType = metric.appearance.type;
  }
  const config = { ...DEFAULT_VISUAL_CONFIG, ...baseConfig, type: appearanceType };

  const { primaryColor: companyPrimaryColor } = UseCompanyColors();
  const isAdmin = userRole === "admin";
  const { appId, ownerWorkspaceId, gameConfig } = metric;
  const isGamifiedLocally = gameConfig?.status === "live";
  const targetValue = gameConfig?.dailyTarget || 100;
  /** Integrated = from connected app (not manual); always use company color for graph */
  const isIntegrated = Boolean(appId && appId !== "manual") || metric._source === "integration" || metric._integration;

  const appById = APPS.find((a) => a.id === appId);
  const manualApp = APPS.find((a) => a.id === "manual");
  const sourceApp = appById || manualApp;
  const integrationApp = integrationApps.find((a) => (a.code || a.id) === appId);
  const appIcon = integrationApps.length > 0 && appId
    ? getLogoFromIntegrationApps(integrationApps, appId)
    : sourceApp?.icon;
  const appName = integrationApp?.name || sourceApp?.name || "Metric";
  const workspaceList = allWorkspaces.length > 0 ? allWorkspaces : WORKSPACES;
  const ownerWorkspace = workspaceList.find((w) => w.id === ownerWorkspaceId);
  const isWorkspaceMetric = ownerWorkspaceId !== "GLOBAL";

  // For company KPI metrics, groupId = workspace id (_id). Show that workspace in the assign bar.
  const workspacesToShow = useMemo(() => {
    if (metric._source === "companyKpi" && metric.ownerWorkspaceId && metric.ownerWorkspaceId !== "GLOBAL") {
      const ws = workspaceList.find((w) => w.id === metric.ownerWorkspaceId);
      return ws ? [ws] : [];
    }
    return assignedWorkspaces;
  }, [metric._source, metric.ownerWorkspaceId, workspaceList, assignedWorkspaces]);

  const formatValue = (val) => {
    if (metric.unit === "currency") return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(val);
    if (metric.unit === "percent") return `${val.toFixed(1)}%`;
    return val.toLocaleString();
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) setShowMenu(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const renderLiveBadge = () => {
    if (isGlobalView || config.badgeVisible === false) return null;
    return (
      <span className="flex-shrink-0 flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-emerald-50 text-[10px] font-semibold text-emerald-600 border border-emerald-100 tracking-wider">
        LIVE
        <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse shadow-[0_0_6px_rgba(16,185,129,0.4)]" />
      </span>
    );
  };

  const renderGamifiedBadge = () => {
    if (isGlobalView || !isGamifiedLocally) return null;
    return (
      <span
        className="flex-shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-lg bg-[var(--gmb-primary-soft)] text-[var(--gmb-primary)]"
        title="Gamified metric"
        aria-label="Gamified metric"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5">
          <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" />
          <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" />
          <path d="M4 22h16" />
          <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22" />
          <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22" />
          <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z" />
        </svg>
      </span>
    );
  };

  const renderChart = () => {
    if (config.type === "number" || config.type === "text-only") return null;
    const data = metric.sparklineData;
    const baseColor = companyPrimaryColor || (isIntegrated ? undefined : config.primaryColor);
    const color = baseColor || "var(--gmb-primary)";
    const opacity = isGamifiedLocally ? 0.8 : (config.chartOpacity ?? 0.6);

    // Progress bar visualization
    if (config.type === "progress") {
      const percentage = Math.min(Math.round((metric.currentValue / targetValue) * 100), 100);
      return (
        <div className="mt-6 w-full space-y-2">
          <div className="h-2 w-full bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full transition-all duration-1000 ease-out rounded-full"
              style={{
                width: `${percentage}%`,
                backgroundColor: color,
                opacity,
              }}
            />
          </div>
          <div className="flex justify-between items-center text-xs font-medium text-gray-400">
            <span>0</span>
            <span>{formatValue(targetValue)} Goal</span>
          </div>
        </div>
      );
    }

    // Radial / ring visualization
    if (config.type === "radial") {
      const ringData = [
        { name: "Value", value: metric.currentValue },
        { name: "Remaining", value: Math.max(targetValue - metric.currentValue, 0) },
      ];
      return (
        <div className="h-32 w-full mt-4 flex items-center justify-center">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={ringData}
                innerRadius="65%"
                outerRadius="90%"
                paddingAngle={4}
                dataKey="value"
                startAngle={225}
                endAngle={-45}
                stroke="none"
              >
                <Cell fill={color} fillOpacity={opacity} />
                <Cell fill="#F3F4F6" />
              </Pie>
            </PieChart>
          </ResponsiveContainer>
        </div>
      );
    }

    // Thermometer / goal visualization
    if (config.type === "thermometer") {
      const percentage = Math.min((metric.currentValue / targetValue) * 100, 100);
      return (
        <div className="h-28 w-12 mx-auto mt-4 bg-gray-100 rounded-full p-1 relative border border-gray-100 shadow-inner">
          <div
            className="absolute bottom-1 inset-x-1 rounded-full transition-all duration-1000"
            style={{
              height: `calc(${percentage}% - 8px)`,
              backgroundColor: color,
              opacity,
            }}
          />
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-[10px] font-medium text-gray-900 bg-white/80 px-1 rounded shadow-sm">
              {percentage.toFixed(0)}%
            </span>
          </div>
        </div>
      );
    }

    // Bar / line / sparkline / area variants all collapse to the same
    // smoothed sparkline used on /company-metrics so cards across the app
    // share one visual rhythm. Color is the brand color lightened 25% so the
    // line reads as soft texture, not a saturated foreground element.
    const lightColor = baseColor ? lighten(baseColor, 0.25) : "var(--gmb-primary)";
    return (
      <div className="w-full mt-4">
        <MetricSparkline data={data} color={lightColor} height={88} />
      </div>
    );
  };

  const cornerClasses = { none: "rounded-none", default: "rounded-xl", large: "rounded-[32px]", full: "rounded-[48px]" };
  const shadowClasses = { none: "shadow-none", soft: "shadow-sm", medium: "shadow-md" };
  const cornerForCard = ({ none: "rounded-none", default: "rounded-xl", large: "rounded-[32px]", full: "rounded-[48px]" })[
    (metric.visualConfig || DEFAULT_VISUAL_CONFIG).cornerRadius || "default"
  ];
  // Unified value size — was text-3xl (30px) / text-2xl / text-5xl. Now all
  // sit at 24px / 500 to match the company metrics card scale.
  const valueSizeMap = { small: "text-[20px]", medium: "text-[24px]", large: "text-[28px]" };
  const valueSizeClass = isGamifiedLocally ? "text-[24px]" : valueSizeMap[config.valueSize || "medium"];

  const getGamifiedDisplay = () => {
    if (!isGamifiedLocally) return null;
    let numberPart = "";
    let unitPart = "";
    const isMonetary = gameConfig?.valueType === "monetary";

    if (gameConfig?.type === "anchor") {
      const anchorNum = gameConfig.anchorValue || 0;
      if (gameConfig.anchorUnit === "percent") {
        numberPart = `${anchorNum}%`;
      } else if (isMonetary) {
        numberPart = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(anchorNum);
      } else {
        numberPart = anchorNum.toLocaleString();
      }
      unitPart = "anchor";
    } else {
      const targetNum = gameConfig?.dailyTarget || 0;
      if (isMonetary) {
        numberPart = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(targetNum);
      } else {
        numberPart = targetNum.toLocaleString();
      }
      const freq = gameConfig?.resetFrequency;
      if (freq === "weekly") unitPart = "per week";
      else if (freq === "monthly") unitPart = "per month";
      else unitPart = "per day";
    }

    return (
      <div className={`flex flex-col ${config.valuePosition === "center" ? "items-center" : "items-start"}`}>
        <span className={`font-semibold text-gray-900 tracking-tight transition-all duration-300 ${valueSizeClass} leading-none`}>
          {numberPart}
        </span>
        <span className="text-[12px] font-medium text-gray-500 mt-1.5">
          {unitPart}
        </span>
      </div>
    );
  };

  return (
    <div onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)} className={`bg-transparent transition-all group relative h-full min-w-0 flex flex-col ${cornerForCard} ${isPreview ? "pointer-events-none" : "cursor-pointer"}`}>
      <div className={`flex-grow flex flex-col bg-white border relative h-full min-w-0 transition-shadow duration-200 ${isGamifiedLocally && !isPreview ? "border-[var(--gmb-primary-tint)] group-hover:border-[var(--gmb-primary-tint)]" : "border-gray-100"} ${cornerForCard} ${isPreview ? "" : "group-hover:shadow-md"}`}>
        <div className="p-6 flex flex-col h-full">
          <div className="flex justify-between items-start gap-2 mb-6 min-w-0">
            <div className={`flex items-center space-x-4 overflow-hidden flex-grow min-w-0 min-w-[3.5rem] mr-0 ${config.badgePosition === "inline-title" ? "flex-row-reverse justify-end gap-2 space-x-0" : ""}`}>
              {(sourceApp || appIcon) && (
                <div className="flex-shrink-0 w-10 h-10 rounded-xl overflow-hidden flex items-center justify-center bg-gray-50 border border-gray-100 p-2">
                  <img src={appIcon} alt={appName} className="w-full h-full object-contain" />
                </div>
              )}
              <div className="flex flex-col overflow-hidden min-w-0 flex-grow">
                <div className="flex items-center gap-2">
                  <h3 className="text-[14px] font-semibold text-gray-900 truncate tracking-tight leading-none">{metric.name}</h3>
                  {config.badgePosition === "inline-title" && renderLiveBadge()}
                </div>
                <p className="text-[11px] text-gray-400 truncate mt-1.5 leading-none font-medium">
                  {(() => {
                    if (!isGlobalView || !isWorkspaceMetric) return ``;
                    if (metric._source === "companyKpi") return `Workspace: ${ownerWorkspace?.name || "Workspace"}`;
                    return `Originally created in ${ownerWorkspace?.name || "Workspace"}`;
                  })()}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 min-w-0 flex-shrink pt-1 overflow-visible">
              <div className="flex items-center gap-1.5">
                {config.badgePosition !== "inline-title" && renderLiveBadge()}
                {renderGamifiedBadge()}
              </div>

              {!isPreview && (
                <div className="relative flex-shrink-0" ref={menuRef}>
                  <button type="button" onClick={(e) => { e.stopPropagation(); setShowMenu(!showMenu); }} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-50 text-gray-400 hover:text-gray-600 transition-colors opacity-40 group-hover:opacity-100" aria-label="Open metric menu">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="w-5 h-5">
                      <circle cx="12" cy="12" r="1" />
                      <circle cx="12" cy="5" r="1" />
                      <circle cx="12" cy="19" r="1" />
                    </svg>
                  </button>
                  {showMenu && (
                    <div className="absolute right-0 mt-2 w-64 rounded-lg py-2 z-[50] overflow-hidden" style={FROSTED_POPOVER_STYLE}>
                      {isGamifiedLocally && (
                        <button type="button" onClick={(e) => { e.stopPropagation(); setShowRules(true); setShowMenu(false); }} className="w-full flex items-center px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors text-left group/menu">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 mr-3 text-gray-400">
                            <line x1="6" x2="10" y1="11" y2="11" />
                            <line x1="8" x2="8" y1="9" y2="13" />
                            <line x1="15" x2="15.01" y1="13" y2="13" />
                            <line x1="18" x2="18.01" y1="11" y2="11" />
                            <path d="M17.32 5H6.68a4 4 0 0 0-3.978 3.59c-.006.052-.01.101-.017.152C2.604 9.416 2 14.456 2 16a3 3 0 0 0 3 3c1 0 1.5-.5 2-1l1.414-1.414A2 2 0 0 1 9.828 16h4.344a2 2 0 0 1 1.414.586L17 18c.5.5 1 1 2 1a3 3 0 0 0 3-3c0-1.544-.604-6.584-.685-7.258-.007-.05-.011-.1-.017-.151A4 4 0 0 0 17.32 5z" />
                          </svg>
                          Game rules
                        </button>
                      )}
                      {isAdmin && (
                        <>
                          <button type="button" onClick={() => { onEdit?.("appearance"); setShowMenu(false); }} className="w-full flex items-center px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors text-left">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 mr-3 text-gray-400">
                              <path d="M12 20h9M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
                            </svg>
                            Appearance
                          </button>
                          <button type="button" onClick={() => { onEdit?.("gamification"); setShowMenu(false); }} className="w-full flex items-center px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors text-left">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 mr-3 text-gray-400">
                              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                            </svg>
                            {isGlobalView ? "Usage & Insights" : "Gamification"}
                          </button>
                          <button type="button" onClick={(e) => { e.stopPropagation(); setShowDetails(true); setShowMenu(false); }} className="w-full flex items-center px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors text-left">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 mr-3 text-gray-400">
                              <circle cx="12" cy="12" r="10" />
                              <path d="M12 16v-4M12 8h.01" />
                            </svg>
                            Metric details
                          </button>
                          {isGlobalView && (
                            <button type="button" onClick={() => { onGamifyForWorkspace?.(); setShowMenu(false); }} className="w-full flex items-center px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors text-left">
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 mr-3 text-gray-400">
                                <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z" />
                                <path d="M20 3v4" />
                                <path d="M22 5h-4" />
                                <path d="M4 17v2" />
                                <path d="M5 18H3" />
                              </svg>
                              Gamify for workspace
                            </button>
                          )}
                          {isGlobalView ? (
                            <button type="button" onClick={() => { onAssign?.(); setShowMenu(false); }} className="w-full flex items-center px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors text-left border-t border-gray-50 mt-1">
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 mr-3 text-gray-400">
                                <path d="M12 4v16m8-8H4" />
                              </svg>
                              Assign to workspace
                            </button>
                          ) : (
                            <div className="h-px bg-gray-50 my-1 mx-4" />
                          )}
                          {metric._source !== "companyKpi" && (
                            <button type="button" onClick={() => { onRemove?.(); setShowMenu(false); }} className="w-full flex items-center px-4 py-2.5 text-sm font-medium text-pink-600 hover:bg-pink-50 transition-colors text-left">
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 mr-3">
                                <path d="M3 6h18" />
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                              </svg>
                              {isGlobalView ? "Remove metric" : "Disconnect / Remove metric"}
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className={`flex flex-col ${config.valuePosition === "center" ? "items-center text-center" : ""}`}>
            <div className={`flex items-baseline gap-2 ${config.valuePosition === "center" ? "flex-col items-center gap-0" : ""}`}>
              {isGamifiedLocally ? (
                getGamifiedDisplay()
              ) : (
                <span className={`font-semibold text-gray-900 tracking-tight transition-all duration-300 ${valueSizeClass}`}>
                  {formatValue(metric.currentValue)}
                </span>
              )}
              {!isGamifiedLocally && config.showTrend && (
                <span className={`text-[12px] font-semibold flex items-center ${metric.trend >= 0 ? "text-emerald-500" : "text-pink-500"}`}>
                  {config.showArrow && (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" className={`w-3 h-3 mr-0.5 ${metric.trend < 0 ? "rotate-180" : ""}`}>
                      <path d="M5 15l7-7 7 7" />
                    </svg>
                  )}
                  {Math.abs(metric.trend)}%
                </span>
              )}
            </div>
            {config.showDescription && metric.description && (
              <p className="text-[11px] text-gray-500 leading-relaxed mt-1.5">{metric.description}</p>
            )}
            {renderChart()}
          </div>
        </div>

        {!isPreview && isGlobalView && isAdmin && (
          <div className="absolute inset-x-0 bottom-0 h-14 px-6 bg-white/95 backdrop-blur-md opacity-0 group-hover:opacity-100 transition-all duration-300 flex items-center justify-between border-t border-gray-100 shadow-[0_-4px_20px_-10px_rgba(0,0,0,0.1)]">
            <button type="button" onClick={(e) => { e.stopPropagation(); onAssign?.(); }} className="flex items-center space-x-2 text-[11px] font-semibold text-[var(--gmb-primary)] hover:text-[var(--gmb-primary-hover)] bg-[var(--gmb-primary-soft)] px-3.5 py-2 rounded-xl border border-[var(--gmb-primary-tint)] transition-all active:scale-95">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" className="w-3.5 h-3.5">
                <path d="M12 5v14M5 12h14" />
              </svg>
              <span>Assign</span>
            </button>
            <div className="flex items-center space-x-1 overflow-hidden ml-4">
              {workspacesToShow.length > 0 ? (
                <div className="flex items-center -space-x-1.5 overflow-hidden">
                  {workspacesToShow.slice(0, 2).map((ws, i) => (
                    <div key={`${metric.dashboardId}-ws-chip-${ws.id}`} className="px-2.5 py-1 rounded-full bg-white border border-gray-100 text-[10px] font-semibold text-gray-600 shadow-sm whitespace-nowrap z-[10] border-[var(--gmb-primary-tint)]" style={{ zIndex: 10 - i }}>
                      {ws.name}
                    </div>
                  ))}
                  {workspacesToShow.length > 2 && (
                    <div className="px-1.5 py-1 rounded-full bg-gray-100 border border-gray-200 text-[10px] font-semibold text-gray-500 shadow-sm z-[0]">+{workspacesToShow.length - 2} more</div>
                  )}
                </div>
              ) : (
                <span className="text-xs font-medium text-gray-400 italic opacity-60">No workspaces yet</span>
              )}
            </div>
          </div>
        )}

        {!isPreview && !isGlobalView && isAdmin && !isGamifiedLocally && (
          <div className="absolute inset-x-0 bottom-0 h-14 px-6 bg-white/95 backdrop-blur-md opacity-0 group-hover:opacity-100 group-hover:translate-y-0 translate-y-2 transition-all duration-300 flex items-center border-t border-gray-100 shadow-[0_-4px_20px_-10px_rgba(0,0,0,0.1)]">
            <button type="button" onClick={(e) => { e.stopPropagation(); onGamify?.(); }} className="flex items-center space-x-2 text-[12px] font-medium text-white bg-[var(--gmb-primary)] hover:bg-[var(--gmb-primary-hover)] px-4 py-2 rounded-xl transition-all active:scale-95 shadow-md shadow-[var(--gmb-primary-soft)]">
              <PremiumRobotIcon className="w-4 h-4 mr-0.5" />
              <span>Gamify this metric</span>
            </button>
          </div>
        )}
      </div>

      <RuleModal
        isOpen={showRules}
        onClose={() => setShowRules(false)}
        metric={metric}
        canEdit={isAdmin && !isPreview}
        onSaveGuideText={onSaveGuideText}
        onSaveDailyGoalText={onSaveDailyGoalText}
      />
      <MetricDetailsModal
        isOpen={showDetails}
        onClose={() => setShowDetails(false)}
        metric={metric}
        allMetrics={allMetrics}
        rawKpi={rawKpi}
        rawKpis={rawKpis}
        integrationApps={integrationApps}
      />

      <style>{`
        .rotate-y-active { animation: spin-y-smooth 2.5s linear infinite; }
        @keyframes spin-y-smooth { from { transform: perspective(400px) rotateY(0deg); } to { transform: perspective(400px) rotateY(360deg); } }
      `}</style>
    </div>
  );
}

export default MetricCard;
